import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { visibleStaffNav } from '../staffNav';

export const PANEL_NAV_HEIGHT = 38;

export const ASKFRANK_TINT = '#3B4C8A';

export const DESK_INPUT_CLASS =
    'w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-maroon';

export const DESK_BUTTON_CLASS =
    "border border-gray-300 bg-gray-100 px-3 py-[6px] font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gray-700 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40";

export const DESK_PRIMARY_CLASS =
    "px-4 py-[6px] font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40";

export const DESK_PRIMARY_STYLE = { boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)', backgroundColor: ASKFRANK_TINT };

export const DESK_DANGER_CLASS =
    "bg-red-700 px-3 py-[6px] font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40";

export const TINY_BUTTON_CLASS =
    'border border-gray-300 bg-white px-2 py-[2px] text-[10px] uppercase text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40';

export function AskFrankTabs({ userData }) {
    const location = useLocation();

    const links = useMemo(() => {
        const section = visibleStaffNav(userData).find(item => item.key === 'admin');
        const group = section?.groups?.find(entry => entry.header === 'AskFrank');
        return group?.links || [];
    }, [userData]);

    const tabClass = (isActive) => `relative -mb-px border border-b-0 px-4 pt-[6px] pb-[7px] font-['Jersey_10'] text-[17px] uppercase leading-none tracking-[1px] transition-colors ${
        isActive
            ? 'z-10 border-gray-300 bg-offwhite text-maroon'
            : 'border-black/30 bg-black/25 text-white/70 hover:bg-black/15 hover:text-white'
    }`;

    return (
        <div className="flex flex-wrap items-end gap-1 border-b border-gray-300 pl-1">
            {links.map(link => (
                <Link key={link.path} to={link.path} className={tabClass(location.pathname === link.path)}>
                    {link.label}
                </Link>
            ))}
        </div>
    );
}

export function DeskRibbon({ children }) {
    return (
        <div
            className="sticky z-20 flex flex-wrap items-center gap-3 border border-gray-300 bg-offwhite px-3 py-2"
            style={{ top: PANEL_NAV_HEIGHT, boxShadow: '0 3px 0 0 rgba(0,0,0,0.5)' }}
        >
            {children}
        </div>
    );
}

export function DeskTitle({ children }) {
    return <span className="font-['Jersey_10'] text-[19px] uppercase tracking-[1px] text-maroon">{children}</span>;
}

export function DeskNote({ children }) {
    return <span className="text-[12px] text-gray-500">{children}</span>;
}

export function DeskPanel({ title, right, padding = 'p-3', tint = ASKFRANK_TINT, className = '', children }) {
    return (
        <section className={`border border-gray-300 bg-offwhite ${className}`} style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.5)' }}>
            {(title || right) && (
                <header className="flex items-center justify-between gap-2 px-3 py-[6px]" style={{ backgroundColor: tint }}>
                    <h3 className="font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-white">{title}</h3>
                    {right}
                </header>
            )}
            <div className={padding}>{children}</div>
        </section>
    );
}

export function DeskField({ label, hint, required, className = '', children }) {
    return (
        <div className={`block ${className}`}>
            <div className="mb-1 flex items-baseline gap-2">
                <span className="font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gray-700">{label}</span>
                {required && <span className="text-[11px] text-maroon">required</span>}
                {hint && <span className="text-[11px] italic text-gray-400">{hint}</span>}
            </div>
            {children}
        </div>
    );
}

export function DeskStat({ label, value, tone = 'default', hint }) {
    const toneClass = {
        default: 'text-gray-900',
        warn: 'text-orange-600',
        bad: 'text-red-600',
        good: 'text-green-700',
    }[tone] || 'text-gray-900';

    return (
        <div className="flex min-w-[110px] flex-1 flex-col border border-gray-200 bg-white px-3 py-2">
            <span className="font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gray-500">{label}</span>
            <span className={`font-['Jersey_10'] text-[26px] leading-none ${toneClass}`}>{value}</span>
            {hint && <span className="mt-1 text-[11px] text-gray-400">{hint}</span>}
        </div>
    );
}

export function DeskEmpty({ children }) {
    return <p className="py-8 text-center text-sm uppercase text-gray-500">{children}</p>;
}

export function Pill({ children, tone = 'gray', title }) {
    const tones = {
        gray: 'bg-gray-100 text-gray-700',
        green: 'bg-green-100 text-green-900',
        yellow: 'bg-yellow-100 text-yellow-900',
        orange: 'bg-orange-100 text-orange-900',
        red: 'bg-red-100 text-red-900',
        blue: 'bg-blue-100 text-blue-900',
        solid: 'bg-maroon text-white',
    };

    return (
        <span title={title} className={`whitespace-nowrap px-2 py-[1px] text-[10px] font-bold uppercase ${tones[tone] || tones.gray}`}>
            {children}
        </span>
    );
}

export const formatNumber = (value) => (value === null || value === undefined || value === '')
    ? '-'
    : Number(value).toLocaleString('en-GB');

export const formatDate = (value) => {
    if (!value) return 'never';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 'never' : parsed.toLocaleDateString('en-GB');
};

export const formatDateTime = (value) => {
    if (!value) return 'never';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 'never' : parsed.toLocaleString('en-GB');
};

function AskFrankDesk({ userData, children }) {
    return (
        <div className="flex flex-col gap-3">
            <AskFrankTabs userData={userData} />
            {children}
        </div>
    );
}

export default AskFrankDesk;
